/**
 * Declarative preset model.
 *
 * Phase 1 keeps this deliberately small: every value is JSON-friendly so
 * presets can later be loaded from disk without code changes. No callbacks,
 * no closures, no WGSL snippets here — those are Phase 3 concerns per the
 * plan.
 */

import type { SeedParams } from '../../renderer/webgpu/passes/seedPass';
import type { WarpParams } from '../../renderer/webgpu/passes/feedbackWarpPass';
import { WarpKind } from '../../renderer/webgpu/passes/feedbackWarpPass';
import type { WarpFieldConfig } from '../warpField/types';
import { MirrorMode } from '../../renderer/webgpu/passes/compositePass';

export { WarpKind, MirrorMode };
export type { WarpFieldConfig };

export const AUDIO_SOURCES = [
  'time',
  'rms',
  'bass',
  'mid',
  'treble',
  'beatConfidence',
] as const;
export type AudioSource = (typeof AUDIO_SOURCES)[number];

export const TRANSFORM_KINDS = [
  'linear',
  'sine',
  'smoothPulse',
  'expFalloff',
] as const;
export type TransformKind = (typeof TRANSFORM_KINDS)[number];

/**
 * A modulation binding adds a per-frame contribution to a base parameter.
 *
 *   contribution = transform(source) * amount + (offset ?? 0)
 *   final        = base + contribution                      (after smoothing)
 *
 * The base value lives in `seed.params` / `warp.params`. Bindings stack
 * (multiple bindings on the same target sum together).
 */
export type ModulationBinding = {
  target: ModulationTarget;
  source: AudioSource;
  transform: TransformKind;
  amount: number;
  /** For time-based transforms (sine, expFalloff) — Hz / decay rate. */
  rate?: number;
  /** Low-pass smoothing time constant (ms). 0 / undefined => no smoothing. */
  smoothingMs?: number;
  /** DC offset added on top of `transform(source) * amount`. */
  offset?: number;
};

export type SeedConfig = {
  kind: 'waveform';
  params: SeedParams;
};

/**
 * The warp config carries `kind` separately from the params bag; on upload
 * the engine combines them into the GPU-side WarpParams struct.
 *
 * `fieldT` and `fieldStrength` are runtime-managed by the engine (driven by
 * the WarpFieldSystem when one is active for the preset), so they're not
 * authored by preset writers.
 */
export type WarpConfig = {
  kind: WarpKind;
  params: Omit<WarpParams, 'kind' | 'fieldT' | 'fieldStrength'>;
};

export type TransitionConfig = {
  /** Duration of the param crossfade when this preset becomes active. */
  durationMs: number;
};

/**
 * Per-preset overrides for the present pass. Anything left undefined keeps
 * the engine's current value; set what you want to change.
 */
export type PresentConfig = {
  /** Enable bloom for this preset; usually paired with bloomIntensity > 0. */
  bloomEnabled?: boolean;
  /** Additive bloom weight (0..~2). Higher = more glowing halos. */
  bloomIntensity?: number;
  /** Bloom bright-pass threshold (linear light). Lower = more pixels glow. */
  bloomThreshold?: number;
  /** Bloom soft-knee width above the threshold. */
  bloomKnee?: number;
  /** Blur tap-stride multiplier; >1 widens the bloom. */
  bloomSpread?: number;
  /** Vignette amount (0..1). Override of the engine default. */
  vignette?: number;
  /** Film-grain amount (0..0.1ish). */
  grain?: number;
};

/** Per-preset composite config (purely-spatial transforms). */
export type CompositeConfig = {
  /** Mirror mode applied after warp+seed. */
  mirror?: MirrorMode;
  /** Number of segments for kaleidoscope (default 6). */
  kaleidoscopeSegments?: number;
  /** Contrast multiplier; 1 = identity. */
  contrast?: number;
  /** Brightness offset; 0 = identity. */
  brightness?: number;
};

export type VisualizerPreset = {
  id: string;
  name: string;
  seed: SeedConfig;
  warp: WarpConfig;
  modulation: ModulationBinding[];
  transition: TransitionConfig;
  /**
   * Optional. When present, the engine instantiates a WarpFieldSystem that
   * the warp shader samples (only when `warp.kind === WarpKind.Field`).
   * Procedural-only presets leave this undefined.
   */
  warpField?: WarpFieldConfig;
  /** Optional per-preset present-pass overrides (bloom, grain, vignette). */
  present?: PresentConfig;
  /** Optional per-preset composite config (mirror, kaleidoscope, contrast). */
  composite?: CompositeConfig;
};

// ---- Modulation target registry ---------------------------------------------
//
// Adding a new target here is a one-line change. Keep this list ordered by
// scope (warp.* then seed.*) for readable HUD output.

export const MODULATION_TARGETS = [
  'warp.baseZoom',
  'warp.bassZoomGain',
  'warp.rotation',
  'warp.bassRotationGain',
  'warp.ripple',
  'warp.rippleFreq',
  'warp.decay',
  'warp.chroma',
  'warp.hueShift',
  'warp.beatPunch',
  'warp.centerDriftAmp',
  'warp.centerDriftRate',
  'seed.baseY',
  'seed.gain',
  'seed.halfThick',
  'seed.intensity',
  'seed.color.r',
  'seed.color.g',
  'seed.color.b',
  'seed.edgeSharp',
] as const;
export type ModulationTarget = (typeof MODULATION_TARGETS)[number];
